import { useTranslation } from "react-i18next";
import { CButton, CFormSelect } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilX } from "@coreui/icons";
import { offersForLine, type UsableOfferDto } from "../../../api/hooks/useOffers";
import { discountLabel } from "../../../lib/offer-copy";
import { linesToFree, savingOnLine } from "../../../lib/offer-savings";
import { formatPriceUsd } from "../../../lib/money";
import { useCartStore, type StrategyCartItem } from "../../../state/cartStore";

interface CartLineOfferProps {
  item: StrategyCartItem;
  offers: UsableOfferDto[];
}

/**
 * The offer on one cart line: what is on it, and the picker when there is a choice to make.
 *
 * Whether a line takes the single offer that fits it is not decided here — the cart decides that for
 * every line at once, because a capped offer has to land where it is worth most.
 */
export function CartLineOffer({ item, offers }: CartLineOfferProps) {
  const { t } = useTranslation("offers");
  const strategyItems = useCartStore((s) => s.strategyItems);
  const setLineOffer = useCartStore((s) => s.setLineOffer);

  const fitting = offersForLine(offers, item.strategyId, item.billingCycle);
  const chosen = fitting.find((offer) => offer.id === item.offerId) ?? null;

  if (fitting.length === 0) return null;

  const choose = (offerId: string | null) => {
    const offer = fitting.find((o) => o.id === offerId);
    const freeFrom = offer
      ? linesToFree(strategyItems, offer, item.strategyId).map((line) => line.strategyId)
      : [];

    setLineOffer(item.strategyId, offerId, freeFrom, offer?.maxStrategies ?? null);
  };

  if (chosen) {
    const displaced = displacedNames(strategyItems, chosen, item.strategyId);

    return (
      <div className="mt-2">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className="badge rounded-pill text-bg-success-subtle border border-success px-3 py-2">
            <strong>{chosen.publicName}</strong> ·{" "}
            {t("cart.saves", { amount: formatPriceUsd(savingOnLine(chosen, item.priceUsd)) })}
          </span>
          <CButton
            color="link"
            size="sm"
            className="p-0 text-body-secondary"
            onClick={() => choose(null)}
            aria-label={t("cart.removeOffer", { offer: chosen.publicName })}
          >
            <CIcon icon={cilX} />
          </CButton>
          {fitting.length === 1 && (
            <span className="small text-body-secondary">{t("cart.onlyOfferThatFits")}</span>
          )}
        </div>
        {displaced.length > 0 && (
          <div className="small text-body-secondary mt-1">
            {t("cart.movedFrom", { strategies: displaced.join(", ") })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="d-flex align-items-center gap-2 flex-wrap mt-2">
      <span className="small text-body-secondary">{t("cart.offerLabel")}</span>
      <CFormSelect
        size="sm"
        className="w-auto mw-100"
        value=""
        aria-label={t("cart.chooseFor", { strategy: item.name })}
        onChange={(e) => choose(e.target.value === "" ? null : e.target.value)}
      >
        <option value="">{t("cart.chooseCount", { count: fitting.length })}</option>
        {fitting.map((offer) => (
          <option key={offer.id} value={offer.id}>
            {t("cart.option", {
              name: offer.publicName,
              discount: discountLabel(offer, t),
              amount: formatPriceUsd(savingOnLine(offer, item.priceUsd)),
            })}
          </option>
        ))}
      </CFormSelect>
    </div>
  );
}

/** The lines this offer was taken off to land here, so the cart can say what that cost. */
function displacedNames(
  items: StrategyCartItem[],
  offer: UsableOfferDto,
  targetStrategyId: string
): string[] {
  if (offer.maxStrategies === null) return [];

  const others = items.filter(
    (item) => item.offerId === offer.id && item.strategyId !== targetStrategyId
  );

  return others.length >= offer.maxStrategies ? others.map((item) => item.name) : [];
}
